import { AgChartsReact } from 'ag-charts-react';
import React, { useMemo } from 'react';
import { ChildGradeReport } from '../../typings/api-types';
import { AgCartesianSeriesTooltipRendererParams, AgChartOptions, AgSeriesTooltip, time } from 'ag-charts-community';
import _ from 'lodash';
import { isoDateToCommonFormat } from '../../shared/helpers/dateHelper';

type ChildEducationGradeReportChartProps = {
  gradeReports: ChildGradeReport[];
};

type ChildGradeReportForPlotting = ChildGradeReport & {
  js_date: Date;
  // 0 when the report is on file but has no GPA to plot, so it can be marked on the bottom axis
  no_gpa_marker?: number;
};

// Shared by both series so hovering a point or a cross reads the same way
const tooltip: AgSeriesTooltip<AgCartesianSeriesTooltipRendererParams<ChildGradeReportForPlotting>> = {
  renderer: (params) => ({
    title: isoDateToCommonFormat(params.datum.date) ?? undefined,
    content:
      params.datum.gpa === null || params.datum.gpa === undefined
        ? 'Report on file, no GPA'
        : 'GPA: ' + params.datum.gpa,
  }),
};

const getOptions = (gradeReports: ChildGradeReport[]): AgChartOptions => {
  const gradeReportsForPlotting = gradeReports.map((gradeReport) => {
    const clonedReport = _.clone(gradeReport) as ChildGradeReportForPlotting;
    clonedReport.js_date = new Date(gradeReport.date);
    clonedReport.no_gpa_marker = gradeReport.gpa === null || gradeReport.gpa === undefined ? 0 : undefined;
    return clonedReport;
  });
  return {
    autoSize: true,
    data: gradeReportsForPlotting,
    theme: {
      overrides: {
        line: {
          series: {
            highlightStyle: {
              series: {
                strokeWidth: 3,
                dimOpacity: 0.2,
              },
            },
          },
        },
      },
    },
    series: [
      {
        type: 'line',
        xKey: 'js_date',
        xName: 'Date',
        yKey: 'gpa',
        yName: 'GPA',
        stroke: '#000000',
        marker: {
          stroke: '#000000',
          fill: '#000000',
        },
        tooltip,
      },
      {
        type: 'scatter',
        xKey: 'js_date',
        xName: 'Date',
        yKey: 'no_gpa_marker',
        yName: 'Report on file, no GPA',
        marker: { shape: 'cross', size: 12, stroke: '#888888', fill: '#888888' },
        tooltip,
      },
    ],
    axes: [
      {
        position: 'bottom',
        type: 'time',
        tick: {
          interval: time.month.every(3),
        },
        title: {
          text: 'Date',
        },
        label: {
          autoRotate: true,
          // https://www.ag-grid.com/javascript-charts/axes-labels/#time-formatting
          format: '%m %b %Y',
        },
      },
      {
        position: 'left',
        type: 'number',
        min: 0,
        max: 4,
        title: {
          text: 'GPA (max 4.0)',
        },
        label: {
          autoRotate: true,
        },
      },
    ],
  };
};

const ChildEducationGradeReportChart = (props: ChildEducationGradeReportChartProps) => {
  // Rebuilt whenever the reports change (for example after adding one), so the chart shows new data
  const options = useMemo(() => getOptions(props.gradeReports), [props.gradeReports]);
  return <AgChartsReact options={options} />;
};

export default ChildEducationGradeReportChart;
